import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  buildCronFromConfig,
  DAY_OF_WEEK_OPTIONS,
  describeSchedule,
  TIMEZONE_OPTIONS,
  validateScheduleConfig,
  type ScheduleConfig,
  type ScheduleKind,
} from '../../utils/jobScheduleUtils';

type JobScheduleEditorProps = {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  config: ScheduleConfig;
  onChange: (config: ScheduleConfig) => void;
};

const FREQUENCY_OPTIONS: { value: ScheduleKind; label: string }[] = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekly', label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
  { value: 'custom', label: 'Custom (advanced)' },
];

export const JobScheduleEditor: React.FC<JobScheduleEditorProps> = ({
  enabled,
  onEnabledChange,
  config,
  onChange,
}) => {
  const validationError = useMemo(() => {
    if (!enabled) return null;
    return validateScheduleConfig(config);
  }, [enabled, config]);

  const previewCron = useMemo(() => {
    if (!enabled) return null;
    return buildCronFromConfig(config);
  }, [enabled, config]);

  const previewText = useMemo(() => {
    if (!enabled) return 'Automatic runs are off. Use “Run now” on the job card when needed.';
    if (validationError) return null;
    return describeSchedule(true, previewCron, config.timezone);
  }, [enabled, validationError, previewCron, config.timezone]);

  const setKind = (kind: ScheduleKind) => {
    onChange({ ...config, kind });
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch checked={enabled} onChange={(e) => onEnabledChange(e.target.checked)} />
        }
        label="Run automatically on a schedule"
        sx={{ mb: 2, display: 'block' }}
      />

      <Collapse in={enabled}>
        <Box display="flex" flexDirection="column" gap={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Frequency</InputLabel>
            <Select
              label="Frequency"
              value={config.kind}
              onChange={(e) => setKind(e.target.value as ScheduleKind)}
            >
              {FREQUENCY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {config.kind === 'weekly' ? (
            <FormControl fullWidth size="small">
              <InputLabel>Day of week</InputLabel>
              <Select
                label="Day of week"
                value={config.dayOfWeek}
                onChange={(e) =>
                  onChange({ ...config, dayOfWeek: Number(e.target.value) })
                }
              >
                {DAY_OF_WEEK_OPTIONS.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          {config.kind === 'monthly' ? (
            <FormControl fullWidth size="small">
              <InputLabel>Day of month</InputLabel>
              <Select
                label="Day of month"
                value={config.dayOfMonth}
                onChange={(e) =>
                  onChange({ ...config, dayOfMonth: Number(e.target.value) })
                }
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          {config.kind !== 'custom' ? (
            <TextField
              fullWidth
              size="small"
              label="Time"
              type="time"
              value={config.time}
              onChange={(e) => onChange({ ...config, time: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="24-hour format"
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              label="Cron expression"
              value={config.cronExpression}
              onChange={(e) => onChange({ ...config, cronExpression: e.target.value })}
              helperText="Five fields: minute hour day month weekday. Example: 0 2 * * *"
              placeholder="0 2 * * *"
            />
          )}

          <FormControl fullWidth size="small">
            <InputLabel>Timezone</InputLabel>
            <Select
              label="Timezone"
              value={config.timezone}
              onChange={(e) => onChange({ ...config, timezone: e.target.value })}
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {validationError ? (
            <Alert severity="warning">{validationError}</Alert>
          ) : previewText ? (
            <Alert severity="info" icon={false}>
              <Typography variant="body2">{previewText}</Typography>
              {previewCron && config.kind !== 'custom' ? (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  Cron: {previewCron}
                </Typography>
              ) : null}
            </Alert>
          ) : null}
        </Box>
      </Collapse>

      {!enabled ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {previewText}
        </Typography>
      ) : null}
    </Box>
  );
};

export default JobScheduleEditor;
